import { useMemo } from 'react'
import { Activity, CheckCircle2, Clock, ArrowRight } from 'lucide-react'

import type { ConsultationSummary } from '@/services/consultation.service'
import { useClinicFormat } from '@/context/ClinicTimezoneContext'

interface DiagnosisSpan {
  code: string
  firstDate: string
  lastDate: string
  active: boolean // present in the most recent signed note
  occurrences: number
}

/**
 * Diagnosis timeline derived from the signed progress notes: for each ICD-10
 * code, when it first appeared, when it was last recorded, and whether it is
 * still active (present in the latest note). Lets the clinician see when a
 * problem was diagnosed and when it resolved or changed.
 */
export function DiagnosisTimeline({ notes }: { notes: ConsultationSummary[] }) {
  const { formatDate } = useClinicFormat()

  const spans = useMemo<DiagnosisSpan[]>(() => {
    const chrono = [...notes].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    )
    if (chrono.length === 0) return []
    const latest = chrono[chrono.length - 1]
    const latestCodes = new Set(latest.diagnosis_codes ?? [])

    const map = new Map<string, DiagnosisSpan>()
    for (const note of chrono) {
      for (const code of note.diagnosis_codes ?? []) {
        const existing = map.get(code)
        if (existing) {
          existing.lastDate = note.created_at
          existing.occurrences += 1
        } else {
          map.set(code, {
            code,
            firstDate: note.created_at,
            lastDate: note.created_at,
            active: latestCodes.has(code),
            occurrences: 1,
          })
        }
      }
    }
    // Active first, then by most recent appearance.
    return [...map.values()].sort((a, b) => {
      if (a.active !== b.active) return a.active ? -1 : 1
      return new Date(b.lastDate).getTime() - new Date(a.lastDate).getTime()
    })
  }, [notes])

  if (spans.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <Activity className="h-8 w-8 text-ml-on-surface-variant/20" />
        <p className="text-xs italic text-ml-on-surface-variant/50">
          Sin diagnósticos CIE-10 registrados en notas firmadas.
        </p>
      </div>
    )
  }

  const activeCount = spans.filter((s) => s.active).length
  const resolvedCount = spans.length - activeCount

  return (
    <div className="space-y-3">
      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-2">
        {activeCount > 0 && (
          <span className="flex items-center gap-1 rounded bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {activeCount} activo{activeCount > 1 ? 's' : ''}
          </span>
        )}
        {resolvedCount > 0 && (
          <span className="flex items-center gap-1 rounded bg-ml-surface-container-high px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-ml-on-surface-variant/60">
            <CheckCircle2 className="h-3 w-3" />
            {resolvedCount} resuelto{resolvedCount > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Diagnosis rows */}
      <ul className="space-y-2">
        {spans.map((s) => (
          <li
            key={s.code}
            className={`group relative overflow-hidden rounded-xl border transition-shadow hover:shadow-sm ${
              s.active
                ? 'border-emerald-200/60 bg-emerald-50/40'
                : 'border-ml-outline-variant/10 bg-ml-surface'
            }`}
          >
            {/* Accent left bar */}
            <div
              className={`absolute inset-y-0 left-0 w-1 rounded-l-xl ${
                s.active ? 'bg-emerald-500' : 'bg-ml-on-surface-variant/20'
              }`}
              aria-hidden
            />

            <div className="flex items-center gap-3 px-4 py-3 pl-5">
              {/* Code chip */}
              <div className="flex-shrink-0">
                <span
                  className={`inline-block rounded-md px-2 py-0.5 font-mono text-xs font-bold tracking-tight ${
                    s.active
                      ? 'bg-emerald-600/10 text-emerald-800'
                      : 'bg-ml-surface-container-high text-ml-on-surface-variant'
                  }`}
                >
                  {s.code}
                </span>
              </div>

              {/* Status + dates */}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider ${
                      s.active ? 'text-emerald-700' : 'text-ml-on-surface-variant/50'
                    }`}
                  >
                    {s.active ? 'Activo' : 'Resuelto'}
                  </span>
                  {s.occurrences > 1 && (
                    <span className="text-[10px] text-ml-on-surface-variant/40">
                      · {s.occurrences} menciones
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-[10px] text-ml-on-surface-variant/60">
                  <Clock className="h-2.5 w-2.5 shrink-0" />
                  <span>{formatDate(s.firstDate)}</span>
                  {s.lastDate !== s.firstDate && (
                    <>
                      <ArrowRight className="h-2.5 w-2.5 shrink-0" />
                      <span>{formatDate(s.lastDate)}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function DiagnosisTimelineIcon() {
  return <Activity className="h-4 w-4 text-ml-primary" />
}
